'use client';

import { useEffect, useRef, useState, type CSSProperties } from 'react';
import { PortalLink, useCart } from '../../portal';
import { BlockIcon } from '../icons/sidebarIcons';

export interface CartQuickViewProps {
  label: string;
  cartHref: string;
  checkoutHref: string;
  shopHref: string;
  icon: string;
  style: CSSProperties;
}

export function CartQuickView({
  label,
  cartHref,
  checkoutHref,
  shopHref,
  icon,
  style,
}: CartQuickViewProps) {
  const cartState = useCart();
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const lines = cartState?.cart?.lines ?? [];
  const count = cartState?.lineCount ?? 0;

  useEffect(() => {
    if (!open) return;
    const closeOutside = (event: PointerEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) setOpen(false);
    };
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };
    document.addEventListener('pointerdown', closeOutside);
    document.addEventListener('keydown', closeOnEscape);
    return () => {
      document.removeEventListener('pointerdown', closeOutside);
      document.removeEventListener('keydown', closeOnEscape);
    };
  }, [open]);

  if (!cartState?.available) {
    return (
      <PortalLink
        href={cartHref}
        className="lms-header-action-btn lms-header-cart-btn"
        aria-label={label}
        style={{ ...style, ...triggerStyle }}
      >
        <span
          aria-hidden
          className="lms-header-action-icon"
          style={fallbackIconStyle}
        >
          <BlockIcon value={icon} size={22} />
        </span>
        <span className="lms-sr-only">{label}</span>
      </PortalLink>
    );
  }

  return (
    <div ref={rootRef} className="lms-cart-quick-view" style={{ position: 'relative', flexShrink: 0 }}>
      <button
        type="button"
        className="lms-header-action-btn lms-header-cart-btn"
        aria-label={count > 0 ? `${label}, ${count} ${count === 1 ? 'item' : 'items'}` : label}
        aria-haspopup="dialog"
        aria-expanded={open}
        onClick={() => setOpen((current) => !current)}
        style={{ ...style, ...triggerStyle }}
      >
        <span
          aria-hidden
          className="lms-header-action-icon"
          style={iconAnchorStyle}
        >
          <BlockIcon value={icon} size={22} />
          {count > 0 ? <span style={badgeStyle}>{count > 99 ? '99+' : count}</span> : null}
        </span>
        <span className="lms-sr-only">{label}</span>
      </button>

      {open ? (
        <div role="dialog" aria-label={`${label} quick view`} style={panelStyle}>
          <div style={panelHeadingStyle}>
            <strong>{label}</strong>
            <span style={countStyle}>{count === 1 ? '1 item' : `${count} items`}</span>
          </div>

          {lines.length === 0 ? (
            <div style={emptyStyle}>
              <p style={{ margin: 0 }}>Your basket is empty.</p>
              <PortalLink href={shopHref} onClick={() => setOpen(false)} style={textLinkStyle}>
                Start shopping
              </PortalLink>
            </div>
          ) : (
            <>
              <ul style={lineListStyle}>
                {lines.slice(0, 4).map((line) => (
                  <li key={line.id} style={lineStyle}>
                    <span style={lineDescriptionStyle}>{line.description}</span>
                    <span style={lineMetaStyle}>
                      {line.quantity} × {cartState?.cart?.currency} {line.unitPrice}
                    </span>
                  </li>
                ))}
              </ul>
              {lines.length > 4 ? (
                <p style={moreStyle}>+ {lines.length - 4} more</p>
              ) : null}
              <div style={totalStyle}>
                <span>Total</span>
                <strong>{cartState?.cart?.currency} {cartState?.cart?.total}</strong>
              </div>
              <div style={actionsStyle}>
                <PortalLink href={cartHref} onClick={() => setOpen(false)} style={secondaryStyle}>
                  View basket
                </PortalLink>
                <PortalLink href={checkoutHref} onClick={() => setOpen(false)} style={primaryStyle}>
                  Checkout
                </PortalLink>
              </div>
            </>
          )}
        </div>
      ) : null}
    </div>
  );
}

const badgeStyle: CSSProperties = {
  position: 'absolute',
  insetBlockStart: -9,
  insetInlineEnd: -11,
  display: 'grid',
  placeItems: 'center',
  minWidth: 18,
  height: 18,
  padding: '0 4px',
  border: '2px solid var(--color-surface-card, #fff)',
  borderRadius: 999,
  background: 'var(--color-accent-600, #dc2626)',
  color: '#fff',
  fontSize: 10,
  fontWeight: 800,
  lineHeight: 1,
  pointerEvents: 'none',
};

const triggerStyle: CSSProperties = {
  position: 'relative',
  width: 44,
  height: 44,
  minWidth: 44,
  minHeight: 44,
  padding: 0,
  justifyContent: 'center',
  borderRadius: 'var(--radius-md, 8px)',
  cursor: 'pointer',
};

const iconAnchorStyle: CSSProperties = {
  position: 'relative',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 24,
  height: 24,
  margin: 0,
};

const fallbackIconStyle: CSSProperties = {
  ...iconAnchorStyle,
  width: 44,
  height: 44,
};

const panelStyle: CSSProperties = {
  position: 'absolute',
  insetBlockStart: 'calc(100% + 12px)',
  insetInlineEnd: 0,
  zIndex: 45,
  width: 'min(380px, calc(100vw - 24px))',
  overflow: 'hidden',
  border: '1px solid var(--color-border-subtle, #e5e7eb)',
  borderRadius: 'var(--radius-lg, 10px)',
  background: 'var(--color-surface-card, #fff)',
  color: 'var(--color-text-primary, #111827)',
  boxShadow: 'var(--shadow-xl, 0 24px 48px rgba(0,0,0,.14))',
};
const panelHeadingStyle: CSSProperties = {
  display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 16,
  padding: '16px 18px', borderBottom: '1px solid var(--color-border-subtle, #e5e7eb)',
};
const countStyle: CSSProperties = { color: 'var(--color-text-secondary, #6b7280)', fontSize: 13 };
const emptyStyle: CSSProperties = { display: 'grid', gap: 12, padding: '28px 18px', textAlign: 'center' };
const textLinkStyle: CSSProperties = { color: 'var(--color-text-link, #1d4ed8)', fontWeight: 650 };
const lineListStyle: CSSProperties = { listStyle: 'none', margin: 0, padding: '4px 18px' };
const lineStyle: CSSProperties = { display: 'grid', gap: 3, padding: '12px 0', borderBottom: '1px solid var(--color-border-subtle, #e5e7eb)' };
const lineDescriptionStyle: CSSProperties = { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: 14, fontWeight: 650 };
const lineMetaStyle: CSSProperties = { color: 'var(--color-text-secondary, #6b7280)', fontSize: 12 };
const moreStyle: CSSProperties = { margin: 0, padding: '6px 18px 10px', color: 'var(--color-text-secondary, #6b7280)', fontSize: 12 };
const totalStyle: CSSProperties = { display: 'flex', justifyContent: 'space-between', gap: 16, padding: '14px 18px', borderTop: '1px solid var(--color-border-subtle, #e5e7eb)' };
const actionsStyle: CSSProperties = { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, padding: '0 18px 18px' };
const actionStyle: CSSProperties = { minHeight: 42, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', borderRadius: 'var(--radius-button, 8px)', fontSize: 14, fontWeight: 700, textDecoration: 'none' };
const secondaryStyle: CSSProperties = { ...actionStyle, border: '1px solid var(--color-border-default, #d1d5db)', color: 'var(--color-text-primary, #111827)' };
const primaryStyle: CSSProperties = { ...actionStyle, background: 'var(--color-brand-600, #2563eb)', color: 'var(--color-text-on-brand, #fff)' };